import { Avatar, Button, Divider, Stack, Typography } from '@mui/material';
import { DatePickerInput } from 'Components/HookFormInputs/DatePickerInput';
import { PriceInput } from 'Components/HookFormInputs/PriceInput';
import { TextInput } from 'Components/HookFormInputs/TextInput';
import { ToggleButtons } from 'Components/HookFormInputs/ToggleButtons';
import { defaultMinDate } from 'Config';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useIsDesktop } from 'Hooks';
import { Duplicate, MinusButton } from 'Images';
import { IExpenseClaim } from 'Interfaces/expenseClaim';
import { illegalChars } from 'Regex';
import { useFormContext } from 'react-hook-form';

interface IForm {
  onDuplicate: () => void;
  onFormClose: () => void;
  formWrapperRef: React.RefObject<HTMLDivElement>;
}

export const Form = ({ onDuplicate, onFormClose, formWrapperRef }: IForm) => {
  const isDesktop = useIsDesktop();
  const dictionary = useDictionary(Dictionary, DICTIONARY.ExpenseClaim);

  const {
    register,
    watch,
    formState: { errors },
  } = useFormContext<IExpenseClaim>();

  return (
    <Stack
      border='1px solid rgba(0, 0, 0, 0.08)'
      borderRadius='12px'
      boxShadow='0px 4px 24px 0px rgba(45, 112, 217, 0.16)'
      gap={'24px'}
      ref={formWrapperRef}
      maxWidth={isDesktop ? '770px' : undefined}
      bgcolor={'white'}
    >
      <Stack flexDirection='row' p={'16px'} gap={'12px'}>
        <Button
          size='small'
          sx={{
            p: '0',
            height: '24px',
            width: '24px',
            minWidth: '24px',
            border: '0.5px solid rgba(244, 244, 244, 1)',
            borderRadius: '6px',
          }}
          onClick={onFormClose}
        >
          <Avatar
            sx={{ height: '32px', width: '32px' }}
            src={MinusButton}
            alt={dictionary.minimizeForm}
          />
        </Button>
        <Typography
          fontWeight={700}
          fontSize={isDesktop ? '18px' : '16px'}
          lineHeight={'24px'}
          component={'h2'}
        >
          {dictionary.cardTitle}
        </Typography>
      </Stack>
      <Divider
        sx={{ m: '-24px 24px', borderColor: 'primary.shade_100' }}
        flexItem
        aria-hidden='true'
      />
      <Stack
        maxWidth={isDesktop ? '576px' : undefined}
        p={isDesktop ? '16px 88px 40px' : '16px'}
        gap={'24px'}
        m={'0 auto'}
        sx={{ '.MuiInputLabel-root': { color: 'text.primary' } }}
      >
        <DatePickerInput
          label={dictionary.tripDate}
          registerName='travelDate'
          maxDate={new Date()}
          minDate={defaultMinDate}
        />
        <fieldset>
          <Stack gap='16px'>
            <legend>{dictionary.usedPublicTransport}</legend>
            <Typography variant='body5'>
              {dictionary.usedPublicTransport}
            </Typography>
            <ToggleButtons
              option1={{ id: true, value: dictionary.yes }}
              option2={{ id: false, value: dictionary.no }}
              registerName='isPublicTransport'
              defaultValue={false}
            />
          </Stack>
        </fieldset>
        {watch('isPublicTransport') && (
          <PriceInput
            register={register('publicTransportPrice')}
            errors={(errors as any)?.publicTransportPrice}
            label={dictionary.tripCost}
            shrink={true}
            value={watch('publicTransportPrice') || null}
          />
        )}
        <TextInput
          register={register('originPlace', {
            onChange: (e) => {
              e.target.value = e.target.value.replace(illegalChars, '');
            },
          })}
          errors={(errors as any)?.originPlace}
          label={dictionary.origin}
          shrink
        />
        <TextInput
          register={register('destinationPlace', {
            onChange: (e) => {
              e.target.value = e.target.value.replace(illegalChars, '');
            },
          })}
          errors={(errors as any)?.destinationPlace}
          label={dictionary.destination}
          shrink
        />
        <Button
          variant='text'
          sx={{ justifyContent: 'flex-start', gap: '4px' }}
          onClick={onDuplicate}
        >
          <Avatar
            sx={{ width: '20px', height: '20px' }}
            src={Duplicate}
            alt=''
          />
          <Typography variant='body5'>{dictionary.duplicate}</Typography>
        </Button>
      </Stack>
    </Stack>
  );
};
